// Maps the current route to a MAKAI AI context: a surface label, a friendly
// page title, and a curated list of suggested prompts that match the surface.
//
// Add new surfaces here as new routes are introduced — the floating assistant
// reads from this single source so prompts stay consistent across the app.

export type CopilotSurface = {
  surface: string;
  title: string;
  hint?: string;
  suggestions: string[];
};

const SURFACES: Array<{ test: (path: string) => boolean; build: (path: string) => CopilotSurface }> = [
  // Learner surfaces
  { test: (p) => p.startsWith("/learner/grades"), build: () => ({
      surface: "learner.grades", title: "Grades & Reports",
      hint: "Continuous assessment, exam marks and trend charts.",
      suggestions: [
        "Explain my performance trend",
        "Where am I losing the most marks?",
        "Predict my end-of-term grade",
        "Suggest a revision plan for my weakest subject",
      ],
  }) },
  { test: (p) => p.startsWith("/learner/library"), build: () => ({
      surface: "library", title: "Digital Library",
      hint: "Books, notes, videos, audio lessons and past papers.",
      suggestions: [
        "Recommend related books",
        "Find a past paper for this topic",
        "Summarise the resource I just opened",
        "What should I read next?",
      ],
  }) },
  { test: (p) => p.startsWith("/learner/tree"), build: () => ({
      surface: "learning-tree", title: "Learning Tree",
      suggestions: [
        "Which lesson should I do next?",
        "Why is this lesson locked?",
        "Plan a 1-week study path for me",
      ],
  }) },
  { test: (p) => p.startsWith("/learner/lessons") || /\/learner\/lesson\//.test(p), build: () => ({
      surface: "lesson", title: "Lesson",
      suggestions: [
        "Create revision questions on this lesson",
        "Summarise this lesson in 5 bullets",
        "Make flashcards from this lesson",
        "Explain the hardest concept simply",
      ],
  }) },
  { test: (p) => p.startsWith("/learner/quizzes") || p.startsWith("/learner/quiz"), build: () => ({
      surface: "quiz", title: "Quiz",
      suggestions: ["Explain my mistakes", "Generate a practice quiz on the same topic", "Which topic should I revise first?"],
  }) },
  { test: (p) => p.startsWith("/learner/portfolio"), build: () => ({
      surface: "portfolio", title: "Lifelong Portfolio",
      suggestions: ["Suggest careers based on my portfolio", "Write a strong bio for my profile", "Which skills should I add next?"],
  }) },
  { test: (p) => p.startsWith("/learner/achievements"), build: () => ({
      surface: "achievements", title: "Achievements",
      suggestions: ["Which badge is easiest to unlock next?", "Plan my week to keep my streak alive"],
  }) },
  { test: (p) => p.startsWith("/learner/coach"), build: () => ({
      surface: "revision-coach", title: "Revision Coach",
      suggestions: ["Build a 7-day study plan", "Predict my exam readiness", "Schedule revision reminders"],
  }) },
  { test: (p) => p === "/learner" || p.startsWith("/learner/index"), build: () => ({
      surface: "learner.home", title: "Learner Dashboard",
      suggestions: ["What's my plan for today?", "Where am I falling behind?", "Pick the next mission for me"],
  }) },

  // Teacher surfaces
  { test: (p) => p.startsWith("/teacher/gradebook") || p.startsWith("/teacher/assessments"), build: () => ({
      surface: "gradebook", title: "Gradebook",
      suggestions: [
        "Write report comments for my class",
        "Flag students who dropped 2+ grades",
        "Draft a parent message about results",
        "Suggest interventions for low scorers",
      ],
  }) },
  { test: (p) => p.startsWith("/teacher/attendance"), build: () => ({
      surface: "attendance", title: "Attendance",
      suggestions: [
        "Identify absentee trends this term",
        "Draft an SMS to parents of frequent absentees",
        "Predict learners at risk of dropping out",
      ],
  }) },
  { test: (p) => p.startsWith("/teacher/lessons") || p.startsWith("/teacher/planner") || p.startsWith("/teacher/generator"), build: () => ({
      surface: "lesson-planner", title: "Lesson Planning",
      suggestions: [
        "Generate a lesson plan for tomorrow",
        "Create a worksheet for this topic",
        "Make a marking guide",
        "Differentiate this lesson for slower learners",
      ],
  }) },
  { test: (p) => p.startsWith("/teacher/exams"), build: () => ({
      surface: "exam-builder", title: "Exam Builder",
      suggestions: ["Draft an end-of-term paper", "Generate Bloom's taxonomy analysis", "Write a marking guide"],
  }) },
  { test: (p) => p.startsWith("/teacher/assignments"), build: () => ({
      surface: "assignments", title: "Assignments",
      suggestions: ["Draft an assignment brief", "Write a rubric", "Suggest extension tasks"],
  }) },
  { test: (p) => p.startsWith("/teacher/reports") || p.startsWith("/teacher/report-cards"), build: () => ({
      surface: "report-cards", title: "Report Cards",
      suggestions: ["Write report comments for each learner", "Summarise class performance", "Draft a head-teacher remark"],
  }) },
  { test: (p) => p.startsWith("/teacher/behavior") || p.startsWith("/teacher/behaviour"), build: () => ({
      surface: "behavior", title: "Behaviour Tracking",
      suggestions: ["Draft a positive note home", "Suggest a behaviour plan for this learner"],
  }) },
  { test: (p) => p.startsWith("/teacher/timetable"), build: () => ({
      surface: "timetable", title: "Timetable",
      suggestions: ["Optimise this schedule", "Find a slot for a make-up class", "Detect clashes"],
  }) },
  { test: (p) => p.startsWith("/teacher"), build: () => ({
      surface: "teacher.home", title: "Teacher Workspace",
      suggestions: ["Summarise my day", "What needs my attention?", "Draft a class announcement"],
  }) },

  // Parent surfaces
  { test: (p) => p.startsWith("/parent/fees") || p.startsWith("/parent/payments"), build: () => ({
      surface: "parent.fees", title: "Fees & Payments",
      suggestions: ["Summarise my outstanding balance", "Draft a message to the bursar", "When is my next payment due?"],
  }) },
  { test: (p) => p.startsWith("/parent/homework"), build: () => ({
      surface: "parent.homework", title: "Homework",
      suggestions: ["What homework is due this week?", "How can I help with this assignment?"],
  }) },
  { test: (p) => p.startsWith("/parent/timetable"), build: () => ({
      surface: "parent.timetable", title: "Child Timetable",
      suggestions: ["What's on tomorrow?", "Identify the busiest day"],
  }) },
  { test: (p) => p.startsWith("/parent/transport"), build: () => ({
      surface: "parent.transport", title: "Transport",
      suggestions: ["Where is the bus right now?", "Estimated arrival time"],
  }) },
  { test: (p) => p.startsWith("/parent"), build: () => ({
      surface: "parent.home", title: "Parent Dashboard",
      suggestions: ["Summarise my child this week", "Any alerts I should act on?", "Draft a message to the class teacher"],
  }) },

  // Admin surfaces
  { test: (p) => p.startsWith("/admin/finance") || p.startsWith("/admin/fees") || p.startsWith("/admin/payments") || p.startsWith("/admin/invoices"), build: () => ({
      surface: "admin.finance", title: "Finance",
      suggestions: [
        "Predict unpaid fees this term",
        "Top 10 outstanding balances",
        "Draft a payment reminder",
        "Cash-flow outlook for the next 30 days",
      ],
  }) },
  { test: (p) => p.startsWith("/admin/learners") || /\/admin\/learners?\/[^/]+/.test(p), build: () => ({
      surface: "learner-profile", title: "Learner Profile",
      suggestions: [
        "Summarise this learner",
        "Identify strengths and risks",
        "Draft a parent-teacher meeting note",
        "Suggest interventions",
      ],
  }) },
  { test: (p) => p.startsWith("/admin/staff") || p.startsWith("/admin/teachers"), build: () => ({
      surface: "admin.staff", title: "Staff",
      suggestions: ["Who hasn't submitted lesson plans?", "Compare teacher performance", "Draft a staff briefing"],
  }) },
  { test: (p) => p.startsWith("/admin/attendance"), build: () => ({
      surface: "admin.attendance", title: "School Attendance",
      suggestions: ["Identify absentee trends", "Which class needs intervention?", "Predict next week's attendance"],
  }) },
  { test: (p) => p.startsWith("/admin/reports") || p.startsWith("/admin/intelligence") || p.startsWith("/admin/executive") || p.startsWith("/admin/predictive"), build: () => ({
      surface: "admin.reports", title: "Reports & Analytics",
      suggestions: ["Explain this chart", "Generate an executive summary", "What's the most important insight on this page?"],
  }) },
  { test: (p) => p.startsWith("/admin/timetable"), build: () => ({
      surface: "admin.timetable", title: "Master Timetable",
      suggestions: ["Optimise the schedule", "Find clashes", "Balance teacher workload"],
  }) },
  { test: (p) => p.startsWith("/admin/communications") || p.startsWith("/admin/announcements"), build: () => ({
      surface: "admin.comms", title: "Communications",
      suggestions: ["Draft a school-wide announcement", "Write an emergency alert", "Schedule a reminder"],
  }) },
  { test: (p) => p.startsWith("/admin"), build: () => ({
      surface: "admin.home", title: "School Admin",
      suggestions: ["Which students are at risk?", "Summarise school analytics", "Draft an announcement"],
  }) },
];

const DEFAULT_SURFACE: CopilotSurface = {
  surface: "general",
  title: "MAKAI",
  suggestions: [
    "What can you help me with here?",
    "Summarise this page",
    "What should I do next?",
  ],
};

export function getCopilotSurface(path: string): CopilotSurface {
  for (const s of SURFACES) if (s.test(path)) return s.build(path);
  return DEFAULT_SURFACE;
}

// Routes where the floating button should NOT appear (auth pages, the full
// tutor experience itself, the marketing splash before sign-in).
const HIDDEN_PATHS = ["/auth", "/login", "/signup", "/tutor", "/learner/tutor", "/teacher/tutor", "/parent/tutor", "/admin/tutor"];

export function shouldShowCopilot(path: string): boolean {
  if (path === "/" ) return false;
  return !HIDDEN_PATHS.some((h) => path === h || path.startsWith(h + "/"));
}
